// websiteBuildStatus — Alpine component factory for the website build/publish
// status chip + progress popover. Registered in the bundle (studio-bundle.ts)
// at module-evaluation time so it is always present before Alpine initialises
// any element, eliminating the race that occurred when the factory was defined
// in an inline <script> that HTMX re-executes only after Alpine has already
// tried to initialise the x-data component on a boost-swap.
//
// The factory body is a verbatim port of the inline script that was previously
// in templates/components/website_build_status.templ (aside from the
// stages/phase granular-timeline addition below — Part 1, Task 1.5). No other
// behaviour changes.
//
// stages/phase mirror the run's granular timeline (empty on a legacy/coarse
// run — the popover's `x-if="stages && stages.length"` falls back to the flat
// 4-stage checklist in that case). The popover renders the timeline CLIENT-SIDE
// via an Alpine x-for over `stages` (reading each stage's status directly), so a
// run created AFTER page load (a CMS/management publish) shows its stages as they
// arrive over SSE without a page reload. stageLabels (key -> localized label,
// resolved server-side) provides the row labels AND the deploy-phase filter.
//
// mode is the run's build mode ("publish" | "render", from the run's kind —
// website_build_runs.mode). Only a PUBLISH ships the operator's changes; a
// render (retry, area reconcile, a legacy add, …) rebuilds what is already
// approved. The chip words them differently ("Publishing…" vs "Updating
// website…") and only a publish-mode completion fires cms-build-completed
// (the CMS toasts "published" on it); a render completion fires
// cms-render-completed + the publish-state re-check instead, so a pending
// publish state returns the chip to "Unpublished changes". An unknown mode
// ("" — an older server) is treated as a publish (the historical wording).
import { type BuildStage } from "./build-progress-timeline";
import { signalPublishStateChanged } from "./publish-state-signal";

export const BUILD_COMPLETED_EVENT = "cms-build-completed";
export const RENDER_COMPLETED_EVENT = "cms-render-completed";

export function websiteBuildStatus({
  websiteID,
  mode: initMode,
  state: initState,
  stage: initStage,
  runID: initRunID,
  baseURL,
  dirty: initDirty,
  finishedAt: initFinishedAt,
  stages: initStages,
  phase: initPhase,
  stageLabels: initStageLabels,
}: any) {
  return {
    open: false,
    ty: 0,
    state: initState,
    stage: initStage,
    runID: initRunID,
    mode: (initMode || "") as string, // 'publish' | 'render' | '' (unknown → publish)
    dirty: !!initDirty,
    justPublished: false,
    justUpdated: false,
    lastMode: "" as string, // mode of the run lastOutcome describes
    lastOutcome: "" as string, // 'ok' | 'failed' | ''
    finishedAt: (initFinishedAt || "") as string, // RFC3339 of the last terminal run
    stages: (initStages || []) as BuildStage[],
    phase: (initPhase || "") as string,
    stageLabels: (initStageLabels || {}) as Record<string, string>,
    _jpTimer: null as ReturnType<typeof setTimeout> | null,
    // serverPending: the publish union (DB-derived changes, legal, media,
    // drafts) from GET /api/publish-state — the single state source (plan 2
    // D10), OR'd with the client draft flag in displayState(). An unknown
    // ("couldn't check") state never paints the chip as unpublished.
    serverPending: false,

    async refreshPublishState() {
      try {
        const resp = await fetch(baseURL + "/api/publish-state", { credentials: "same-origin" });
        if (!resp.ok) return;
        const d = await resp.json();
        this.serverPending = !!d.pending && !d.unknown;
      } catch {
        /* advisory: keep the last known state */
      }
    },

    // Locale-aware "N minutes ago" for the last publish, via the browser's
    // Intl.RelativeTimeFormat (reads <html lang>) — no hardcoded English.
    relativeFinishedAt(): string {
      if (!this.finishedAt) return "";
      const ms = new Date(this.finishedAt).getTime();
      if (isNaN(ms)) return "";
      const diffSec = Math.round((ms - Date.now()) / 1000); // negative = past
      const lang = (document.documentElement as HTMLElement).lang || "en";
      const rtf = new (Intl as any).RelativeTimeFormat(lang, { numeric: "auto" });
      const abs = Math.abs(diffSec);
      if (abs < 60) return rtf.format(diffSec, "second");
      if (abs < 3600) return rtf.format(Math.round(diffSec / 60), "minute");
      if (abs < 86400) return rtf.format(Math.round(diffSec / 3600), "hour");
      return rtf.format(Math.round(diffSec / 86400), "day");
    },

    // displayState collapses the build state + dirty/justPublished flags
    // into one pill state. Priority is deliberate: a failed publish must
    // surface even with pending edits, so 'failed' sits ABOVE 'unpublished'.
    displayState(): string {
      const render = this.isRender();
      if (this.state === "queued" || this.state === "running") return render ? "updating" : "publishing";
      if (this.justPublished) return "published";
      if (this.justUpdated) return "updated";
      if (this.state === "failed") return render ? "update_failed" : "failed";
      if (this.dirty || this.serverPending) return "unpublished";
      return "live";
    },

    // isRender: the current run is a render-mode build (not a publish).
    isRender(): boolean {
      return this.mode === "render";
    },

    // settled fires the completion side effects of a run that just reached a
    // terminal state, by mode: a publish fires cms-build-completed (the CMS
    // toasts "published"); a render fires cms-render-completed + the
    // publish-state re-check (no toast — nothing was published). A failure of
    // either sends only the re-check, so publish buttons leave "publishing".
    settled(status: string) {
      if (status === "completed" && !this.isRender()) {
        window.dispatchEvent(new CustomEvent(BUILD_COMPLETED_EVENT));
      } else if (status === "completed") {
        window.dispatchEvent(new CustomEvent(RENDER_COMPLETED_EVENT));
        signalPublishStateChanged();
      } else {
        signalPublishStateChanged();
      }
    },

    // flashSettled shows the transient "Published ✓" / "Updated ✓" for 4s.
    // Cancel-guarded so rapid successive completions don't let an earlier
    // timeout clear the flag out from under a newer one.
    flashSettled() {
      if (this._jpTimer) clearTimeout(this._jpTimer);
      const render = this.isRender();
      this.justPublished = !render;
      this.justUpdated = render;
      this._jpTimer = setTimeout(() => {
        this.justPublished = false;
        this.justUpdated = false;
        this._jpTimer = null;
      }, 4000);
    },

    init() {
      // Reconcile the chip against the server's stale-run logic on
      // every load. The server-rendered hydration (initState) is the
      // instant-paint source of truth; this corrects it when a worker
      // died mid-build (the GET endpoint maps stale queued/running runs
      // to failed/stalled so the user sees Retry, not a permanent spinner).
      this.refreshFromServer();
      this.refreshPublishState();
    },

    stageIndex(s: string): number {
      return (
        ({ translating: 0, preparing: 1, building: 2, deploying: 3 } as Record<string, number>)[
          s
        ] ?? -1
      );
    },

    // refreshFromServer fetches the GET /api/build-status endpoint and
    // reconciles the chip state. This makes the stale-run recovery path
    // live: the server maps a queued/running run older than the staleness
    // threshold to failed/stalled, so the chip shows Retry after a worker
    // crash rather than a permanent spinner. Called on init() and after
    // retryBuild() settles to ensure the chip reflects server truth.
    async refreshFromServer() {
      try {
        // Remember whether the chip was mid-flight BEFORE reconciling, so we
        // can detect a build that finished while this component was not the
        // live SSE listener (e.g. it mounted mid-navigation right after a
        // publish, or the run belongs to a different actor whose SSE never
        // reached this session — website_build_tracker.go targets the actor).
        const wasInFlight = this.state === "queued" || this.state === "running";
        const resp = await fetch(baseURL + "/api/build-status", { credentials: "same-origin" });
        if (!resp.ok) return;
        const d = await resp.json();
        if (!d || !d.status) return;
        this.state = d.status === "idle" ? "idle" : d.status;
        this.stage = d.stage || "";
        if (d.run_id) this.runID = d.run_id;
        else if (d.id) this.runID = d.id;
        if (d.mode !== undefined) this.mode = d.mode || "";
        if (d.finished_at) this.finishedAt = d.finished_at;
        this.stages = d.stages || this.stages;
        this.phase = d.phase || this.phase;
        // Record the last terminal outcome so the popover's last-publish
        // line is correct after a reload.
        if (d.status === "completed" || d.status === "failed") {
          this.lastOutcome = d.status === "completed" ? "ok" : "failed";
          this.lastMode = this.mode;
        }
        // If the poll (not the live SSE path) is what learned the build
        // settled, the header publish button never heard about it: its ONLY
        // "done" trigger is the cms-build-completed window event, which
        // onBuildProgress dispatches but this reconcile path historically did
        // not — leaving the header spinner stuck until a full reload. On an
        // in-flight -> settled transition, fire cms-build-completed ONLY when
        // the run completed (the CMS editor toasts "published" on it); any
        // other settled outcome (failed, stalled) sends the structural
        // publish-state re-check instead, mirroring onBuildProgress, so both
        // publish buttons leave "publishing" without a false success toast.
        // Gated on wasInFlight so a normal idle page load stays a no-op (no
        // needless partial refetch).
        const nowInFlight = this.state === "queued" || this.state === "running";
        if (wasInFlight && !nowInFlight) {
          this.settled(this.state);
        }
      } catch {
        /* network error — chip keeps current state */
      }
    },

    onBuildProgress(detail: any) {
      let d: any;
      try {
        d = JSON.parse(detail.data);
      } catch {
        return;
      }
      if (d.website_id !== websiteID) return;
      // The mode follows the run this event reports (a new run may be a
      // render after a publish, or vice versa). Absent → unknown (publish).
      this.mode = d.mode || "";
      this.state = d.status || this.state;
      this.stage = d.stage || "";
      this.stages = d.stages || this.stages;
      this.phase = d.phase || this.phase;
      if (d.run_id) this.runID = d.run_id;
      if (d.status === "completed") {
        this.lastOutcome = "ok";
        this.lastMode = this.mode;
        this.finishedAt = new Date().toISOString();
        // Transient "Published ✓" / "Updated ✓" reassurance.
        this.flashSettled();
        this.settled("completed");
      } else if (d.status === "failed") {
        this.lastOutcome = "failed";
        this.lastMode = this.mode;
        // No cms-build-completed on failure (that one toasts "published"), so
        // send the structural re-check instead: both publish buttons leave
        // their in-flight "publishing" state (plan 2 Task 13).
        this.settled("failed");
      }
    },

    // retryBuild issues the retry via window.htmx.ajax so that htmx
    // natively processes the HX-Trigger flash-message header returned by
    // the handler (both cms.editor.retry_started on success and
    // cms.editor.retry_failed on failure). Raw fetch() would silently
    // discard those headers, giving the user zero feedback on failure.
    // After the ajax settles, refreshFromServer() reconciles the chip to
    // the true server state.
    async retryBuild() {
      this.open = false;
      try {
        await (window as any).htmx.ajax("POST", baseURL + "/api/build-retry", { swap: "none" });
        // A retry is always a render (kind retry): word it as an update —
        // only once the POST went through; refreshFromServer below then
        // reconciles to the run the server actually reports.
        this.mode = "render";
      } catch {
        /* network error — toast won't fire, chip stays red (wording unchanged) */
      }
      await this.refreshFromServer();
    },
  };
}
